import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: t('nav.home'), href: '/', icon: 'ri-home-4-line' },
    { label: t('nav.spreadsheet'), href: '#spreadsheet', icon: 'ri-file-list-3-line' },
    { label: t('nav.categories'), href: '#categories', icon: 'ri-grid-line' },
    { label: t('nav.guides'), href: '#content', icon: 'ri-book-open-line' },
    { label: t('nav.safety'), href: '#safety', icon: 'ri-shield-check-line' },
    { label: t('nav.faq'), href: '#faq', icon: 'ri-question-answer-line' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50 shadow-sm border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <a
            href="/"
            className="flex items-center gap-2 text-foreground-900 no-underline cursor-pointer"
          >
            <span className="text-lg md:text-xl font-heading font-bold tracking-tight whitespace-nowrap">
              Kakobuy<span className="text-primary-500">Polska</span>
            </span>
          </a>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="px-3 py-2 text-sm text-foreground-600 hover:text-foreground-900 transition-colors rounded-md hover:bg-background-100 whitespace-nowrap cursor-pointer no-underline"
              >
                {link.label}
              </a>
            ))}
            <button
              onClick={() => {
                navigate('/artykul/kakobuy-bezpieczne-zakupy');
                window.scrollTo(0, 0);
              }}
              className="ml-1 px-3 py-2 text-sm text-foreground-600 hover:text-foreground-900 transition-colors rounded-md hover:bg-background-100 whitespace-nowrap cursor-pointer"
            >
              {t('nav.shoppingGuide')}
            </button>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="ml-3 px-4 py-2 text-sm font-medium bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer no-underline"
            >
              Kakobuy Spreadsheet
            </a>
          </div>

          <button
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/70 max-h-[70vh] overflow-y-auto">
          <div className="px-4 py-3 flex flex-col gap-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-foreground-400 px-3 py-1">
              {t('nav.navigation')}
            </p>
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 rounded-md transition-colors cursor-pointer no-underline"
                onClick={() => setMobileOpen(false)}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${link.icon} text-foreground-400`}></i>
                </div>
                {link.label}
              </a>
            ))}

            <button
              onClick={() => {
                setMobileOpen(false);
                navigate('/artykul/kakobuy-bezpieczne-zakupy');
                window.scrollTo(0, 0);
              }}
              className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 rounded-md transition-colors cursor-pointer text-left"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-shopping-bag-3-line text-foreground-400"></i>
              </div>
              {t('nav.shoppingGuide')}
            </button>

            <div className="my-2 border-t border-background-200/50"></div>

            <p className="text-xs font-semibold uppercase tracking-wider text-foreground-400 px-3 py-1">
              {t('nav.categoryLinks')}
            </p>
            {categories.map((cat) => (
              <a
                key={cat.seq}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 rounded-md transition-colors cursor-pointer no-underline"
                onClick={() => setMobileOpen(false)}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-external-link-line text-foreground-400 text-xs"></i>
                </div>
                {cat.name}
              </a>
            ))}

            <div className="my-2 border-t border-background-200/50"></div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="mt-1 px-4 py-2.5 text-sm font-medium text-center bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer no-underline"
            >
              Kakobuy Spreadsheet
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}